import {
  IssueCreationSource,
  IssueType,
  PickerMode,
  useStorageKeys,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  NewIssue as NewIssueBase,
  PanelHeader,
  Spacing,
  TopModalBar,
} from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { MediaPicker } from '../shared/components/media/mediaPicker';
import { localStorageHandler } from '../shared/localStorageHandler';

type AnonymousIssueProps = {
  assetId: string;
  workspaceId: string;
  onBack: () => void;
  onCompleted: () => void;
};

// Caps the content column to match anonymousAsset.tsx's CONTENT_MAX_WIDTH.
const CONTENT_MAX_WIDTH = 800;

/**
 * In-page report/note view for the anonymous asset flow.
 *
 * Replaces the legacy NewIssueModal (a Modal with a fixed 700px container that
 * always overflowed, showing a scrollbar). Renders the shared NewIssue form as a
 * page-level swap-in with the same PanelHeader back chrome as AnonymousRoutine,
 * so it now only scrolls when it genuinely overflows the viewport.
 */
export const AnonymousIssue = ({
  assetId,
  workspaceId,
  onBack,
  onCompleted,
}: AnonymousIssueProps) => {
  const { t } = useTranslation();
  const storageKeys = useStorageKeys();

  // Persist the created issue id so the "drop" success page can list it.
  const trackIssueId = async (issueId: string) => {
    const existing = await localStorageHandler.getItem(
      storageKeys.anonymousIssueIds
    );
    const ids: string[] = existing ? JSON.parse(existing) : [];
    if (!ids.includes(issueId)) {
      ids.push(issueId);
    }
    await localStorageHandler.setItem(
      storageKeys.anonymousIssueIds,
      JSON.stringify(ids)
    );
  };

  const onIssueCreated = async (issueId: string, _type?: IssueType) => {
    // Await the write before notifying the parent so the drop page reads the id.
    await trackIssueId(issueId);
    onCompleted();
  };

  return (
    <View style={styles.page}>
      {/* Constrain header + form to the same max-width as the asset/routine column. */}
      <View style={styles.contentColumn}>
        <PanelHeader
          title={t('asset.header.buttons.addNew')}
          showBackButton
          onBackPress={onBack}
        />

        <View style={styles.formWrapper}>
          <NewIssueBase
            assetId={assetId}
            workspaceId={workspaceId}
            mediaPicker={MediaPicker}
            onIssueCreated={onIssueCreated}
            topBar={TopModalBar}
            bottomBar={(barProps) => (
              <View style={styles.footer}>
                <Button
                  style={styles.saveButton}
                  onPress={
                    !barProps.rightPressDisabled ? barProps.onSave : undefined
                  }
                  state={!barProps.rightPressDisabled ? 'active' : 'default'}
                >
                  {barProps.saveLabel}
                </Button>
              </View>
            )}
            mode={PickerMode.PICKER}
            onCancel={onBack}
            hideAdvancedSettings
            createdFrom={IssueCreationSource.Anonymousweb}
            pickerMode="picker"
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    width: '100%',
    backgroundColor: Colors.role.canvas,
    alignItems: 'center',
  },
  contentColumn: {
    flex: 1,
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    flexDirection: 'column',
  },
  formWrapper: {
    flex: 1,
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: Spacing.baseMargin.base,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
